"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";

import {
  absoluteAssetUrl,
  blogPostUrl,
  type BlogPostStory,
} from "@/lib/blog";
import type { Region } from "@/lib/region";
import { resolveBg } from "@/lib/bg-color";

function ArrowIcon() {
  return (
    <svg
      width="17"
      height="11"
      viewBox="0 0 17 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function FromTheBlogView({
  title,
  intro,
  bgColor,
  posts,
  region,
  cta,
}: {
  title: string;
  intro: string;
  bgColor: string | undefined;
  posts: BlogPostStory[];
  region: Region;
  cta: { label: string; href: string; target?: string } | null;
}) {
  const { bg, onDark } = resolveBg(bgColor, "white");
  const scrollerRef = useRef<HTMLUListElement>(null);
  const cardRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const update = () => {
      const rect = scroller.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      let closest = 0;
      let minDist = Infinity;
      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        const cRect = card.getBoundingClientRect();
        const cCenter = cRect.left + cRect.width / 2;
        const dist = Math.abs(cCenter - center);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      if (scroller.scrollLeft <= 1) closest = 0;
      else if (scroller.scrollLeft >= maxScroll - 1) closest = posts.length - 1;
      setActiveIndex(closest);
      setIsScrollable(scroller.scrollWidth > scroller.clientWidth + 4);
    };
    update();
    scroller.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      scroller.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [posts.length]);

  const cols = posts.length >= 3 ? "[@media(min-width:1024px)]:grid-cols-3" : "";

  return (
    <section className={`${bg} ${onDark ? "text-white" : "text-black"}`}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(3rem,5vw,4.5rem)]">
        {(title || intro) && (
          <div className="flex max-w-[640px] flex-col gap-3">
            {title && (
              <h2
                className={`text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold ${
                  onDark ? "text-white" : "text-purple-05"
                }`}
              >
                {title}
              </h2>
            )}
            {intro && (
              <p
                className={`text-body leading-[1.6] ${
                  onDark ? "text-white/85" : "text-black/65"
                }`}
              >
                {intro}
              </p>
            )}
          </div>
        )}

        <ul
          ref={scrollerRef}
          className={`mt-8 flex snap-x snap-mandatory gap-5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] sm:grid sm:snap-none sm:grid-cols-2 sm:gap-x-8 sm:gap-y-12 sm:overflow-visible [&::-webkit-scrollbar]:hidden ${cols} ${
            title || intro ? "md:mt-12" : ""
          }`}
        >
          {posts.map((post, i) => (
            <BlogCard
              key={post.uuid}
              post={post}
              region={region}
              onDark={onDark}
              cardRef={(el) => {
                cardRefs.current[i] = el;
              }}
            />
          ))}
        </ul>

        {isScrollable && posts.length > 1 && (
          <div className="mt-6 flex justify-center sm:hidden">
            <div className="flex items-center">
              {posts.map((post, i) => (
                <button
                  key={post.uuid}
                  type="button"
                  onClick={() => {
                    cardRefs.current[i]?.scrollIntoView({
                      behavior: "smooth",
                      inline: "center",
                      block: "nearest",
                    });
                  }}
                  aria-label={`Go to post ${i + 1}`}
                  className="flex h-6 min-w-6 cursor-pointer items-center justify-center"
                >
                  <span
                    aria-hidden
                    className={`block h-1.5 rounded-full transition-all duration-300 ${
                      activeIndex === i ? "w-8 bg-purple-03" : "w-2 bg-purple-01"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {cta && (
          <div className="mt-8 flex sm:mt-10">
            <a
              href={cta.href}
              {...(cta.target ? { target: cta.target } : {})}
              className={`text-purple-03 inline-flex items-center gap-[7px] py-2.5 text-body leading-[1.054] font-extrabold hover:underline ${
                onDark ? "text-purple-01" : ""
              }`}
            >
              {cta.label}
              <ArrowIcon />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

function BlogCard({
  post,
  region,
  onDark,
  cardRef,
}: {
  post: BlogPostStory;
  region: Region;
  onDark: boolean;
  cardRef: (el: HTMLLIElement | null) => void;
}) {
  const c = post.content || {};
  const href = blogPostUrl(post.slug, region);
  return (
    <li
      ref={cardRef}
      className="w-[82%] shrink-0 snap-start sm:w-auto"
    >
      <Link
        href={href}
        className={`group flex h-full flex-col gap-4 rounded-[18px] focus-visible:outline-2 focus-visible:outline-offset-2 lg:rounded-[20px] ${
          onDark ? "focus-visible:outline-white" : "focus-visible:outline-purple-03"
        }`}
      >
        <div className="bg-dusk-01 relative aspect-4/3 w-full overflow-hidden rounded-[18px] lg:rounded-[20px]">
          {c.featured_image?.filename ? (
            <Image
              src={absoluteAssetUrl(c.featured_image.filename) || ""}
              alt={c.featured_image.alt || post.name}
              fill
              sizes="(min-width: 1115px) 360px, (min-width: 720px) 50vw, 100vw"
              className="object-cover"
            />
          ) : (
            <div className="from-dusk-02 to-purple-01 absolute inset-0 bg-linear-to-br" />
          )}
        </div>
        <div className="flex flex-1 flex-col gap-3">
          <h3 className="text-h6 text-purple-05 leading-[1.2] font-bold">
            {c.title || post.name}
          </h3>
          <span className="text-purple-03 mt-auto inline-flex items-center text-body leading-[1.054] font-extrabold group-hover:underline">
            Read more
          </span>
        </div>
      </Link>
    </li>
  );
}
